import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getCoachLeaderLastSeenMap } from "@/lib/coachNewEvidence";

type LeaderEvidenceCounts = Record<string, number>;

interface CoachNewEvidenceCountRow {
  leader_id: string;
  new_count: number;
}

interface GetCoachNewEvidenceCountsRpcClient {
  rpc(
    fn: "get_coach_new_evidence_counts",
    args: { p_last_seen: Record<string, string>; p_window_start: string }
  ): Promise<{
    data: CoachNewEvidenceCountRow[] | null;
    error: unknown;
  }>;
}

export function useCoachNewEvidence() {
  const { user } = useAuth();
  const supabaseWithCoachNewEvidenceRpc = supabase as unknown as GetCoachNewEvidenceCountsRpcClient;

  const query = useQuery({
    queryKey: ["coach-new-evidence", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return { total: 0, perLeader: {} as LeaderEvidenceCounts };
      const recentWindowStart = new Date(Date.now() - 1000 * 60 * 60 * 24 * 180).toISOString();

      const lastSeenMap = getCoachLeaderLastSeenMap(user.id);
      const { data: countsData, error } = await supabaseWithCoachNewEvidenceRpc.rpc("get_coach_new_evidence_counts", {
        p_last_seen: lastSeenMap,
        p_window_start: recentWindowStart,
      });
      if (error) throw error;

      const perLeader: LeaderEvidenceCounts = {};
      let total = 0;
      (countsData || []).forEach((row) => {
        const nextCount = Number(row.new_count || 0);
        perLeader[row.leader_id] = nextCount;
        total += nextCount;
      });

      return { total, perLeader };
    },
    staleTime: 60_000,
  });

  return useMemo(
    () => ({
      totalNewEvidence: query.data?.total || 0,
      leadersWithNewEvidenceCount: Object.values(query.data?.perLeader || {}).filter((count) => count > 0).length,
      newEvidenceByLeaderId: query.data?.perLeader || ({} as LeaderEvidenceCounts),
      isLoadingNewEvidence: query.isLoading,
      refetchNewEvidence: query.refetch,
    }),
    [query.data, query.isLoading, query.refetch]
  );
}
